/* ============================================================================
 * th-ajax-forms.js — submit Shopify contact / newsletter forms without a reload
 * ----------------------------------------------------------------------------
 * Progressive enhancement, loaded globally. Covers every {% form 'contact' %}
 * and {% form 'customer' %} in the theme (contact page, newsletters, footers,
 * distributor + event + maker submissions, password page) without touching the
 * sections themselves.
 *
 * How it stays out of the way:
 *  - The listener is DELEGATED ON document, so it fires AFTER a form's own
 *    handler. If a section already AJAXes its form (bh-footer does) it calls
 *    preventDefault first and we bail — no double submit.
 *  - Only same-origin /contact endpoints are touched. Account forms
 *    (/account/login, register, addresses...) set sessions and must keep their
 *    native POST, so they are never matched.
 *  - Forms with target="_blank" (the Mailchimp launch forms) are skipped.
 *  - Opt out of any single form with data-no-ajax.
 *  - No JS, or a network failure -> the browser does the normal POST, so a
 *    message is never silently lost.
 *
 * On success we re-render the form from the response HTML, which means each
 * section's OWN success / error markup is used — nothing is hardcoded here.
 * ========================================================================== */
(function () {
  'use strict';
  if (!window.fetch || !window.FormData || !window.DOMParser) return;

  var SENDING = 'is-sending';

  function isContactEndpoint(form) {
    var action = form.getAttribute('action');
    if (!action) return false;
    if (/^https?:\/\//i.test(action) && action.indexOf(window.location.origin) !== 0) return false;
    var path = action.replace(/^https?:\/\/[^/]+/i, '').split('#')[0].split('?')[0].replace(/\/+$/, '');
    return /(^|\/)contact$/.test(path);
  }

  function eligible(form) {
    return form && form.tagName === 'FORM' &&
           !form.hasAttribute('data-no-ajax') &&
           !form.getAttribute('target') &&
           isContactEndpoint(form);
  }

  /* Locate the same form inside the response document: prefer the enclosing
     section id (stable across the render), fall back to document order. */
  function matchFresh(form, doc) {
    var mine = function (root) {
      return Array.prototype.filter.call(root.querySelectorAll('form'), eligible);
    };
    var host = form.closest('[id^="shopify-section-"], [id^="m-section-"], section[id]');
    if (host && host.id) {
      var freshHost = doc.getElementById(host.id);
      if (freshHost) {
        var within = mine(host), freshWithin = mine(freshHost);
        var i = within.indexOf(form);
        if (i > -1 && freshWithin[i]) return freshWithin[i];
        if (freshWithin.length === 1) return freshWithin[0];
      }
    }
    var all = mine(document), idx = all.indexOf(form);
    return idx > -1 ? mine(doc)[idx] : null;
  }

  function setBusy(form, busy) {
    form.classList.toggle(SENDING, busy);
    if (busy) { form.setAttribute('aria-busy', 'true'); } else { form.removeAttribute('aria-busy'); }
    Array.prototype.forEach.call(
      form.querySelectorAll('button, input[type="submit"]'),
      function (el) { el.disabled = busy; }
    );
  }

  function reveal(form) {
    /* Move focus/attention to whatever the section rendered (success or error). */
    var msg = form.querySelector('[class*="success"], [class*="ok"], [class*="error"], [class*="err"]');
    var target = msg || form;
    if (msg) {
      if (!msg.hasAttribute('role')) msg.setAttribute('role', 'status');
      if (!msg.hasAttribute('tabindex')) msg.setAttribute('tabindex', '-1');
      try { msg.focus({ preventScroll: true }); } catch (e) {}
    }
    var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    var box = target.getBoundingClientRect();
    if (box.top < 0 || box.bottom > window.innerHeight) {
      target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
    }
  }

  function send(form) {
    setBusy(form, true);
    fetch(form.action, {
      method: 'POST',
      body: new FormData(form),
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    })
      .then(function (res) { return res.text(); })
      .then(function (html) {
        var fresh = matchFresh(form, new DOMParser().parseFromString(html, 'text/html'));
        if (!fresh) throw new Error('no matching form in response');
        form.innerHTML = fresh.innerHTML;   /* section's own success/error state */
        setBusy(form, false);
        delete form.dataset.thAjaxBusy;
        reveal(form);
      })
      .catch(function () {
        /* Never lose a submission: hand it back to the browser. */
        delete form.dataset.thAjaxBusy;
        setBusy(form, false);
        form.submit();
      });
  }

  document.addEventListener('submit', function (e) {
    if (e.defaultPrevented) return;          /* a section owns this form already */
    var form = e.target;
    if (!eligible(form)) return;
    if (form.dataset.thAjaxBusy) { e.preventDefault(); return; }
    if (typeof form.reportValidity === 'function' && !form.reportValidity()) return;
    e.preventDefault();
    form.dataset.thAjaxBusy = '1';
    send(form);
  });
})();
